import { Inject, Injectable } from '@angular/core';
import {
  compact,
  defaults,
  find,
  findKey,
  get,
  toInteger,
  transform,
} from 'lodash';

import { Partners } from '@lockton-apps/shared/interfaces';

import {
  AccaClassOfBusinessId,
  ClassOfBusinessId,
  COB_META_DATA,
  CobMeta,
  CobMetaDictionary,
  getMetaForPartner,
  NorwayClassOfBusinessId,
} from './cob-meta';
import { TEMP_COB_DATA, TempCobMeta } from './temp-cob-data';

@Injectable()
export class CobMetaService {
  private readonly overrides = {
    [Partners.ACCA]: {
      cyber: AccaClassOfBusinessId.Cyber,
    },
    [Partners.NORWAY]: {
      'property-damage': NorwayClassOfBusinessId.PropertyDamage,
      crime: NorwayClassOfBusinessId.Crime,
      cyber: NorwayClassOfBusinessId.Cyber,
    },
    [Partners.NORWAY_PI]: {
      'property-damage': NorwayClassOfBusinessId.PropertyDamage,
      crime: NorwayClassOfBusinessId.Crime,
      cyber: NorwayClassOfBusinessId.Cyber,
    },
  };

  cobMeta!: CobMetaDictionary;

  constructor(
    @Inject(COB_META_DATA) private _cobMeta: CobMetaDictionary,
    @Inject(TEMP_COB_DATA) private _tempCobData: TempCobMeta[],
  ) {
    this.cobMeta = defaults(
      {},
      this._cobMeta,
      // TODO: Replace this temp solution to unregistered cobs
      transform(_tempCobData, (result: CobMetaDictionary, cob) => {
        result[cob.id] = {
          id: cob.id.toString(),
          title: cob.name || 'Unnamed Product',
          icon: 'product_thin',
          color: 'grayMedium',
          type: 'policy',
          code: cob.code,
        };
      }),
    );
  }

  getClassOfBusinessMeta(cob: number): CobMeta | undefined {
    return this.cobMeta[cob];
  }

  // TODO
  /**
   * There is an issue with getting cob meta by id because we use same id for some products across partners.
   * We merge all cobs into one object (without separation for partners) and because of it
   * when we try to get a cob meta by id, it returns the first found item on the list.
   * In CG the returned item depends on order of merging in COB_META_DATA_DEFAULT.
   */
  getClassOfBusinessById(id: string, portalId?: Partners): ClassOfBusinessId {
    const overrideCob: number | undefined = get(
      this.overrides,
      compact([portalId, id]),
    );

    if (overrideCob) {
      return overrideCob;
    }

    return toInteger(findKey(this.cobMeta, { id }));
  }

  // TODO
  /**
   * There is an issue with getting cob meta by id because we use same id for some products across partners.
   * We merge all cobs into one object (without separation for partners) and because of it
   * when we try to get a cob meta by id, it returns the first found item on the list.
   * In CG the returned item depends on order of merging in COB_META_DATA_DEFAULT.
   */
  getClassOfBusinessMetaById(
    id: string,
    portalId?: Partners,
  ): CobMeta | undefined {
    const overrideCob: number | undefined = get(
      this.overrides,
      compact([portalId, id]),
    );

    if (overrideCob) {
      return this.getClassOfBusinessMeta(overrideCob) as CobMeta;
    }

    return find(this.cobMeta, { id });
  }

  getClassOfBusinessId(cob: number): string {
    const meta = get<CobMeta | undefined>(this.cobMeta, cob);
    return meta ? meta.id : '';
  }
  /** @deprecated use getClassOfBusinessMetaById   */
  legacyGetClassOfBusinessById(
    id: string,
    partner?: Partners,
  ): ClassOfBusinessId {
    return toInteger(findKey(getMetaForPartner(partner), { id }));
  }

  /** @deprecated use getClassOfBusinessMetaById   */
  legacyGetClassOfBusinessMetaById(
    id: string,
    partner?: Partners,
  ): CobMeta | undefined {
    return find(getMetaForPartner(partner), { id });
  }
}
